import { CreateInsuranceInput, createInsurance, deleteInsurance, getInsurances, Insurance, InsuranceCompany, User , State, InsuranceDetails, NonPartnerInsurance, NonPartnerInsuranceCompany } from '@uplift/api';
import { useNonPartnerInsuranceByStudentIdQuery, useNonPartnerInsuranceCompaniesQuery, useStates } from '@uplift/queries';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateFromDB } from '../Settings/helpers';
import { InsuranceDataType, InsuranceSection } from '../Settings/types';
import { useAlert } from 'react-alert';
import { useL10n } from '@uplift/hooks';
import { useState } from 'react';
import { AddressType } from '@uplift/types';

type InsurancesData = {
  primaryInsurance: InsuranceDataType | null;
  secondaryInsurance: InsuranceDataType | null;
};

export type AddressTypeWithStateProfileType = {
  stateProfile?: State;
} & AddressType;

const transformInsuranceData = ({
  insurance,
  insuranceCompanies,
  userData,
  validationDetails,
  states
}: {
  insurance: Insurance;
  insuranceCompanies: InsuranceCompany[];
  userData: User[];
  validationDetails: InsuranceDetails[];
  states: State[];
}): InsuranceDataType => {
  const companyName = insuranceCompanies.find((company) => company.id === insurance.companyId)?.name ?? '';
  const planId = insuranceCompanies.find((company) => company.id === insurance.companyId)?.payorId ?? '';
  const stateProfile = states.find((state) => state.id === insurance.address.stateId);
  const user = userData?.find((user) => user.id === insurance?.createdBy);
  const archivedByUser = userData?.find((user) => user.id === insurance?.archivedBy);
  const insuranceDetails = validationDetails?.find((detail) => detail.insuranceId === insurance?.id) ?? null;

  const getFullName = (user?: User) => {
    if (user?.role === 'superadmin') {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.role === 'patient') {
      return 'Client';
    }

    return 'Uplift Bot';
  };

  return {
    ...insurance,
    address1: insurance.address.line1,
    address2: insurance.address.line2,
    city: insurance.address.city,
    state: insurance.address.stateId,
    zip: insurance.address.zip,
    insuranceCardFront: insurance.cardFrontAssetId,
    insuranceCardBack: insurance.cardBackAssetId,
    companyName,
    planId,
    birthDate: formatDateFromDB(insurance.dob),
    stateProfile,
    createdByFullName: getFullName(user),
    archivedByFullName: getFullName(archivedByUser),
    status: insuranceDetails?.status,
    verifiedBy: insuranceDetails?.verifiedBy ?? null,
    copay: insuranceDetails?.copay ?? null,
    coinsurance: insuranceDetails?.coinsurance ?? null,
    remainingDeductible: insuranceDetails?.remainingDeductible ?? null,
    patientResponsibility: insuranceDetails?.patientResponsibility,
    payorName: insuranceDetails?.payorName,
    updatedAt: insuranceDetails?.updatedAt,
    insuranceTypeCode: insuranceDetails?.insuranceTypeCode,
    isInNetwork: insuranceDetails?.isInNetwork
  };
};


export const useInsurances = (userId: string, suspense = true) => {
  const { states } = useStates(suspense);

  return useQuery({
    queryKey: [ 'insurances', userId ],
    queryFn: async () => {
      const { data: { insurances, insuranceCompanies, userData, validationDetails } } = await getInsurances(userId, true);
      const activeInsurances = insurances.filter((insurance) => !insurance.archivedAt);
      const archivedInsurances = insurances.filter((insurance) => insurance.archivedAt);

      let primaryInsurance: InsuranceDataType | null = null;
      let secondaryInsurance: InsuranceDataType | null = null;

      const primary = activeInsurances.find((insurance) => insurance.isSecondaryInsurance === false);
      const secondary = activeInsurances.find((insurance) => insurance.isSecondaryInsurance === true);

      if (primary) {
        primaryInsurance = transformInsuranceData({
          insurance: primary,
          insuranceCompanies,
          userData,
          validationDetails,
          states
        });
      }
      if (secondary) {
        secondaryInsurance = transformInsuranceData({
          insurance: secondary,
          insuranceCompanies,
          userData,
          validationDetails,
          states
        });
      }

      return {
        primaryInsurance,
        secondaryInsurance,
        archivedInsurances: archivedInsurances.sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map((insurance) => transformInsuranceData({
          insurance,
          insuranceCompanies,
          userData,
          validationDetails,
          states
        })),
      };
    },
    enabled: states.length > 0,
    suspense: suspense
  });
};


export const useSaveInsurance = (userId: string) => {
  const queryClient = useQueryClient();

  const saveInsurance = async (insuranceUpdates: CreateInsuranceInput) => {
    try {
      const res = await createInsurance(insuranceUpdates);

      await queryClient.invalidateQueries([ 'insurances', userId ]);

      return res?.data;
    } catch (error) {
      console.error(error);

      return null;
    }
  };

  return { saveInsurance };
};

type DeleteInsuranceArgs = {
  messages: {
    success: string;
    error: string;
  };
  replaceWithSecondary?: boolean;
  patientId?: string;
  reason?: string;
};

export const useDeleteInsurance = (insuranceSection: InsuranceSection, userId: string, onClose: () => void, archiveReason?: string) => {
  const queryClient = useQueryClient();
  const query = useInsurances(userId);
  const [ loading, setLoading ] = useState(false);
  const alert = useAlert();
  const l10n = useL10n();

  const insurancesData: InsurancesData = query.data ?? {
    primaryInsurance: null,
    secondaryInsurance: null,
  };

  const insuranceData = insurancesData[insuranceSection];

  const deleteInsuranceHandler = async (args: DeleteInsuranceArgs) => {
    setLoading(true);
    try {
      await deleteInsurance(insuranceData?.id ?? '', {
        replaceWithSecondary: Boolean(args.replaceWithSecondary),
        patientId: userId,
        archiveReason: archiveReason
      });

      alert.success(args.messages.success);
      queryClient.invalidateQueries([ 'insurances', userId ]);
      queryClient.invalidateQueries([ 'archivedInsurances', userId ]);

    } catch (error) {
      alert.error(args.messages.error);
    }
    onClose();
  };

  const onDeleteInsurance = (archiveReason?: string) => deleteInsuranceHandler({
    messages: {
      success: l10n.accountSettings.insurance.insuranceDeleted,
      error: l10n.accountSettings.insurance.insuranceCouldNotBeDeleted
    },
    reason: archiveReason
  });

  const onDeletePrimaryAndReplaceWithSecondary = (archiveReason?: string) => deleteInsuranceHandler({
    messages: {
      success: l10n.accountSettings.insurance.insuranceUpdated,
      error: l10n.accountSettings.insurance.insuranceCouldNotBeUpdated
    },
    replaceWithSecondary: true,
    reason: archiveReason
  });

  return { loading, onDeleteInsurance, onDeletePrimaryAndReplaceWithSecondary };
};

const transformNonPartnerInsuranceData = ({
  insurance,
  insuranceCompanies,
  states
}: {
  insurance: NonPartnerInsurance;
  insuranceCompanies: NonPartnerInsuranceCompany[];
  states: State[];
}): InsuranceDataType => {
  const companyName = insuranceCompanies.find((company) => company.id === insurance.companyId)?.name ?? '';
  const stateProfile = states.find((state) => state.id === insurance.holderInfo.address.stateId);

  return {
    ...insurance,
    isSecondaryInsurance: false,
    companyId: insurance.companyId,
    subscriberId: insurance.subscriberId,
    groupNumber: insurance.groupNumber,
    id: insurance.id,
    firstName: insurance.holderInfo.firstName,
    lastName: insurance.holderInfo.lastName,
    isPrimary: insurance.holderType === 'primary',
    address1: insurance.holderInfo.address.line1,
    address2: insurance.holderInfo.address.line2,
    city: insurance.holderInfo.address.city,
    state: insurance.holderInfo.address.stateId,
    zip: insurance.holderInfo.address.zip,
    insuranceCardFront: insurance.cardPhotos.frontAssetId,
    insuranceCardBack: insurance.cardPhotos.backAssetId,
    companyName,
    planId: '',
    birthDate: formatDateFromDB(insurance.holderInfo.dob),
    stateProfile,
    createdByFullName: undefined,
    archivedByFullName: undefined,
    createdAt: insurance.createdAt,
    updatedAt: insurance.updatedAt,
    archivedAt: insurance.archivedAt,

    isNonPartnerInsurance: true,
  };
};


export const useNonPartnerInsurances = (userId: string, suspense = true) => {
  const { states } = useStates(suspense);
  const { data: insurances = [] } = useNonPartnerInsuranceByStudentIdQuery(userId, true, { suspense });
  const { data: insuranceCompanies = [] } = useNonPartnerInsuranceCompaniesQuery(undefined, { suspense });

  const activeInsurances = insurances.filter((insurance) => !insurance.archivedAt);
  const archivedInsurances = insurances.filter((insurance) => insurance.archivedAt);

  let primaryInsurance: InsuranceDataType | null = null;

  const primary = activeInsurances[0];

  if (primary) {
    primaryInsurance = transformNonPartnerInsuranceData({
      insurance: primary,
      insuranceCompanies: insuranceCompanies,
      states
    });
  }

  return {
    data: {
      primaryInsurance,
      archivedInsurances: archivedInsurances.sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map((insurance) => transformNonPartnerInsuranceData({
        insurance,
        insuranceCompanies,
        states
      })),
    },
  };
};